import { isEmpty, isUndefined } from 'lodash';
import {
  tPaymentMethod,
  tPaymentMethodDefaults,
} from '../../SubscriptionManager.types';
import {
  tPaymentMethodRole,
  tPaymentMethodRoles,
} from './PaymentMethodRoles.types';

/**
 * RET-2027 — which saved cards the subscription page labels, and as what.
 *
 * Stripe charges the plan with the card billing resolves as the plan's
 * (`planPaymentMethodId`, rule MO-5 in leadbilling). One-off charges (a wallet
 * top-up, «Pagar periodo», «Renovar ahora») use the customer default instead
 * (`defaultPaymentMethodId`). Since
 * 2025-11-04 "Establecer como preferida" writes both, so the two differ mostly
 * on older accounts — but they do differ, and the page used to show only the
 * customer default.
 *
 * Only a card in the list can be labelled, so an id that is not there resolves
 * to nothing. With no plan card the page stays as it was — the customer
 * default alone, unlabelled — which is why the account's default card is only
 * named next to a plan card it differs from.
 */
export function resolvePaymentMethodRoles(args: {
  paymentMethods: tPaymentMethod[];
  defaults: tPaymentMethodDefaults;
}): tPaymentMethodRoles {
  const findById = (paymentMethodId?: string) =>
    isEmpty(paymentMethodId)
      ? undefined
      : args.paymentMethods.find(
          (method) => method.paymentMethodId === paymentMethodId,
        );

  const planPaymentMethod = findById(args.defaults.planPaymentMethodId);
  if (isUndefined(planPaymentMethod)) {
    return {};
  }

  const customerDefault = findById(
    args.defaults.customerDefaultPaymentMethodId,
  );

  return {
    planPaymentMethod,
    accountDefaultPaymentMethod:
      customerDefault?.paymentMethodId === planPaymentMethod.paymentMethodId
        ? undefined
        : customerDefault,
  };
}

/** The label a saved card carries on the subscription page, if any. */
export function resolvePaymentMethodRole(args: {
  roles: tPaymentMethodRoles;
  paymentMethodId: string;
}): tPaymentMethodRole | undefined {
  if (isEmpty(args.paymentMethodId)) {
    return undefined;
  }
  if (args.roles.planPaymentMethod?.paymentMethodId === args.paymentMethodId) {
    return 'plan';
  }
  if (
    args.roles.accountDefaultPaymentMethod?.paymentMethodId ===
    args.paymentMethodId
  ) {
    return 'accountDefault';
  }
  return undefined;
}
